import secureLocalStorage from 'react-secure-storage';
import { getCenterViewSession, isCenterViewTab } from '@/lib/utils';
import { parseStoredWithExpiration, readSharedStoredRaw } from '@/lib/sharedAuthStorage';

/**
 * 일괄 적용 흐름이 묶이는 세션 — **로그인 토큰**과 그 토큰의 주인(센터·관리자).
 *
 * 흐름은 overlay(라우터·화면 바깥)에 떠 있어서 로그아웃·계정 전환·센터 전환을 넘어 살아남는다.
 * 그래서 열 때·요청 직전·응답 직후·주기적으로 세션을 확인하고, 다르면 닫고 요청을 막는다.
 *
 * 비교하는 두 값:
 *  - 메모리: 이 탭이 요청에 실제로 싣는 토큰과 인증 객체(react-secure-storage 가 탭 시작 때 올려 둔 값).
 *  - 저장소: 탭이 공유하는 localStorage 원문을 복호화한 토큰·인증 객체. 다른 탭의 로그아웃·재로그인이 여기 반영된다.
 *
 * 판정(살아 있음) — 셋 다:
 *  1. 메모리·저장소 토큰이 모두 있고 만료 전이다.
 *  2. **메모리 토큰 = 저장소 토큰.** 다르면 무조건 다른 세션이다. 저장소 쪽 인증 객체의 주인이 같다는 건
 *     토큰 갱신의 증거가 아니다(다른 탭의 늦은 프로필 응답이 옛 주인을 새 토큰 옆에 덮어쓸 수 있다).
 *     같은 탭의 401 갱신은 메모리·저장소를 함께 바꾸므로 영향이 없다. 다른 탭의 갱신으로 닫혀도
 *     서버의 PENDING 작업은 상품 창 최근 작업 줄의 「이어서 끝내기」로 복구된다.
 *  3. 메모리·저장소 인증 객체의 주인이 같고, 연 세션의 주인과 같다.
 * 프로필·구독 정보 저장처럼 인증 객체의 다른 필드만 바뀌면 토큰·주인이 그대로라 닫지 않는다(요청 키 유지).
 *
 * 읽기에는 앱의 getItemWithExpiration 을 쓰지 않는다 — 만료 시 값을 지우고 location.replace 를 부르는 부작용이 있다.
 */

type Auth = { token: string; identity: string; expiresAt: number };

const identityOf = (authObject: any): string | null => {
    if (!authObject || typeof authObject !== 'object' || !authObject.centerId) return null;
    const who = authObject.adminId ?? authObject.id ?? authObject._id ?? authObject.email ?? '';
    return `${authObject.centerId}|${who}`;
};

const parseStored = parseStoredWithExpiration;

const toAuth = (tokenRaw: unknown, authObjectRaw: unknown): Auth | null => {
    const token = parseStored(tokenRaw);
    const authObject = parseStored(authObjectRaw);
    if (!token || !authObject || typeof token.value !== 'string' || !token.value) return null;
    const identity = identityOf(authObject.value);
    if (!identity) return null;
    return { token: token.value, identity, expiresAt: Math.min(token.expirationTime, authObject.expirationTime) };
};

/* 저장소 원문 복호화는 공용(lib/sharedAuthStorage)으로 옮겼다. */
const readStoredRaw = readSharedStoredRaw;

const readStorageAuth = (): Auth | null => toAuth(readStoredRaw('authorization'), readStoredRaw('authObject'));
const readMemoryAuth = (): Auth | null => toAuth(secureLocalStorage.getItem('authorization'), secureLocalStorage.getItem('authObject'));

/** 센터 열람 탭: 세션이 sessionStorage 한 곳뿐이라 메모리·저장소가 같다. */
const readCenterViewAuth = (): Auth | null => {
    const session = getCenterViewSession();
    if (!session) return null;
    const identity = identityOf(session.authObject);
    if (!identity || !session.authorization) return null;
    return { token: String(session.authorization), identity, expiresAt: Number(session.expirationTime) || 0 };
};

/** 지금 이 탭이 쓸 수 있는 세션의 주인(센터|관리자). 판정 규칙은 파일 머리. 아니면 null. */
export const readTemplateCategoryBulkSessionKey = (now: number = Date.now()): string | null => {
    if (isCenterViewTab()) {
        const auth = readCenterViewAuth();
        return auth && auth.expiresAt > now ? auth.identity : null;
    }
    const memory = readMemoryAuth();
    const storage = readStorageAuth();
    if (!memory || !storage) return null;
    if (storage.expiresAt <= now || memory.expiresAt <= now) return null;
    if (memory.token !== storage.token) return null;
    if (memory.identity !== storage.identity) return null;
    return memory.identity;
};

/** 열 때 기억한 세션이 아직 그대로인가. */
export const isTemplateCategoryBulkSessionAlive = (sessionKey: string): boolean =>
    !!sessionKey && readTemplateCategoryBulkSessionKey() === sessionKey;

/** storage 이벤트가 세션과 관련 있나 — clear(키 null) 또는 인증 키. */
export const isTemplateCategoryBulkSessionStorageEvent = (event: StorageEvent): boolean =>
    event.key === null || event.key.endsWith('.authObject') || event.key.endsWith('.authorization');

/** 이 센터의 세션이 지금 살아 있나(화면 표시용). */
export const isTemplateCategoryBulkSessionForCenter = (centerId: string): boolean => {
    const key = readTemplateCategoryBulkSessionKey();
    return !!key && !!centerId && key.startsWith(`${centerId}|`);
};
